import { Modal, Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './limitExceededModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';

/**
 * Mirror of LimitExceededModal but for bots whose Stripe subscription is
 * past_due. Lists every past-due bot and offers an "Update Payment" button
 * per row that opens the Stripe customer portal so the user can fix the
 * card; Stripe then auto-retries the outstanding invoice.
 */
const PaymentPastDueModal = ({
  open,
  onClose,
  bots = [],
  onUpdatePayment,
  updatePaymentLoadingId = null,
}) => {
  const { t } = useTranslation();
  const safeName = (b) => b?.name || b?.domain_name || t('billing_unnamed_bot');

  const handleUpdate = (bot) => {
    try {
      onUpdatePayment?.(bot);
    } catch (e) {
      console.error('Update payment failed', e);
      alert(t('billing_payment_update_error'));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: ['18px', '22px', '22px'],
          width: ['92%', '720px', '760px'],
          maxHeight: '80vh',
          overflowY: 'auto',
          outline: 'none',
        }}
      >
        <div className="modal-content lec-modal">
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
            aria-label="close"
            title={t('close')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>

          <div className="lec-header">
            <div className="lec-title">
              <FontAwesomeIcon
                className="lec-title-icon"
                icon={faCreditCard}
                style={{ color: '#dc2626' }}
              />
              <h2>{t('billing_payment_due_title')}</h2>
            </div>
            <p className="lec-subtitle">
              {t('billing_past_due_subtitle', { count: bots.length })}
            </p>
          </div>

          <div
            className="lec-table-header"
            style={{ gridTemplateColumns: '1.6fr 1.0fr 0.9fr' }}
          >
            <div>{t('billing_col_bot')}</div>
            <div className="lec-col-plan">{t('plan')}</div>
            <div className="lec-col-action">{t('skill_app_action')}</div>
          </div>

          <div className="lec-rows">
            {bots.map((b) => (
              <div
                className="lec-row"
                key={b.id}
                style={{ gridTemplateColumns: '1.6fr 1.0fr 0.9fr' }}
              >
                <div className="lec-bot">
                  <div className="lec-bot-name" title={safeName(b)}>
                    {safeName(b)}
                  </div>
                </div>

                <div className="lec-plan lec-col-plan">
                  <span className="lec-plan-badge">
                    {b?.subscription_plan?.name || 'Free'}
                  </span>
                </div>

                <div className="lec-actions lec-col-action">
                  <button
                    className="continue-button lec-upgrade-btn"
                    onClick={() => handleUpdate(b)}
                    disabled={updatePaymentLoadingId === b.id}
                    title={t('billing_update_payment_tooltip')}
                    style={{
                      backgroundColor: '#dc2626',
                      borderColor: '#dc2626',
                    }}
                  >
                    {updatePaymentLoadingId === b.id ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faCreditCard}
                          style={{ marginRight: 6 }}
                        />
                        {t('billing_update_payment')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default PaymentPastDueModal;
